import { useTranslation } from 'react-i18next';
import { Column, ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { OwnerListItemResponseDto } from '@/features/owners/types/owners-dto';
import ArrowDownIconSolid from '@/components/icons/ArrowDownIconSolid';
import ApartmentIcon from '@/components/icons/ApartmentIcon';
import AgreementIcon from '@/components/icons/AgreementIcon';
import UserIcon from '@/components/icons/UserIcon';
import OwneOwnersStatusDetails from '@/features/owners/components/OwnersStatusDetails';
import EAvatar from '@/components/everridge/EAvatar';
import { useOwnersFilter } from '../hooks/useOwnersFilter';
import { ApiConfig } from '@/config/api.config';

const OwnersPageColumns = (): ColumnDef<OwnerListItemResponseDto>[] => {
	const { t } = useTranslation();

	const { setFilter } = useOwnersFilter();

	const handleSort = (
		columnKey: Capitalize<keyof OwnerListItemResponseDto>,
		column: Column<OwnerListItemResponseDto>,
	) => {
		const newDirection = column.getIsSorted() === 'asc';
		column.toggleSorting(newDirection);

		setFilter({
			orderBy: columnKey,
			orderDirection: newDirection ? 'asc' : 'desc',
		});
	};

	return [
		{
			accessorKey: 'name',
			header: ({ column }) => {
				return (
					<Button
						variant='link'
						className={
							'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
						}
						onClick={() => handleSort('Name', column)}>
						{t('main.owners.table.header.name')}
						<div
							className={`transition-transform duration-50 ease-in-out ${
								column.getIsSorted() === 'asc'
									? '-rotate-180'
									: 'rotate-0'
							}`}>
							{<ArrowDownIconSolid />}
						</div>
					</Button>
				);
			},
			cell: ({ row }) => (
				<div className={'flex items-center gap-2.5'}>
					<EAvatar
						className={'size-8'}
						url={
							row.original.avatarUrl
								? `${ApiConfig.currentUrls.api.slice(0, -1)}${
										row.original.avatarUrl
								  }`
								: undefined
						}
					/>
					<h4 className='text-sm text-popover-800 truncate'>
						{row.getValue('name')}
					</h4>
				</div>
			),
		},
		{
			accessorKey: 'apartments',
			header: ({ column }) => {
				return (
					<Button
						variant='link'
						className={
							'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
						}
						onClick={() => handleSort('Apartments', column)}>
						{t('main.owners.table.header.apartments')}
						<div
							className={`transition-transform duration-50 ease-in-out ${
								column.getIsSorted() === 'asc'
									? '-rotate-180'
									: 'rotate-0'
							}`}>
							{<ArrowDownIconSolid />}
						</div>
					</Button>
				);
			},
			cell: ({ row }) => {
				const { apartments, agreements, trustees } = row.original;

				return (
					<div className={'flex gap-2.5'}>
						<div
							className={
								'flex items-center gap-1.5 text-sm text-popover-800'
							}>
							<ApartmentIcon className={'stroke-popover-800'} />
							{apartments}
						</div>
						<div
							className={
								'flex items-center gap-1.5 text-sm text-popover-800'
							}>
							<AgreementIcon className={'stroke-popover-800'} />
							{agreements}
						</div>
						<div
							className={
								'flex items-center gap-1.5 text-sm text-popover-800'
							}>
							<UserIcon className={'stroke-popover-800'} />
							{trustees}
						</div>
					</div>
				);
			},
		},
		{
			accessorKey: 'email',
			header: ({ column }) => {
				return (
					<Button
						variant='link'
						className={
							'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
						}
						onClick={() => handleSort('Email', column)}>
						{t('main.owners.table.header.email')}
						<div
							className={`transition-transform duration-50 ease-in-out ${
								column.getIsSorted() === 'asc'
									? '-rotate-180'
									: 'rotate-0'
							}`}>
							{<ArrowDownIconSolid />}
						</div>
					</Button>
				);
			},
			cell: ({ row }) => (
				<div className={'text-sm text-popover-800 truncate'}>
					{row.getValue('email')}
				</div>
			),
		},
		{
			accessorKey: 'phoneNumber',
			header: ({ column }) => {
				return (
					<Button
						variant='link'
						className={
							'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
						}
						onClick={() => handleSort('PhoneNumber', column)}>
						{t('main.owners.table.header.phoneNumber')}
						<div
							className={`transition-transform duration-50 ease-in-out ${
								column.getIsSorted() === 'asc'
									? '-rotate-180'
									: 'rotate-0'
							}`}>
							{<ArrowDownIconSolid />}
						</div>
					</Button>
				);
			},
			cell: ({ row }) => (
				<div className={'text-sm text-popover-800 truncate'}>
					{row.getValue('phoneNumber')}
				</div>
			),
		},
		{
			accessorKey: 'language',
			header: ({ column }) => {
				return (
					<Button
						variant='link'
						className={
							'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
						}
						onClick={() => handleSort('Language', column)}>
						{t('main.owners.table.header.language')}
						<div
							className={`transition-transform duration-50 ease-in-out ${
								column.getIsSorted() === 'asc'
									? '-rotate-180'
									: 'rotate-0'
							}`}>
							{<ArrowDownIconSolid />}
						</div>
					</Button>
				);
			},
			cell: ({ row }) => (
				<div className={'text-sm text-popover-800'}>
					{row.getValue('language')}
				</div>
			),
		},
		{
			accessorKey: 'status',
			header: ({ column }) => {
				return (
					<Button
						variant='link'
						className={
							'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
						}
						onClick={() => handleSort('Status', column)}>
						{t('main.owners.table.header.status')}
						<div
							className={`transition-transform duration-50 ease-in-out ${
								column.getIsSorted() === 'asc'
									? '-rotate-180'
									: 'rotate-0'
							}`}>
							{<ArrowDownIconSolid />}
						</div>
					</Button>
				);
			},
			cell: ({ row }) => (
				<div className={'text-sm'}>
					{
						<OwneOwnersStatusDetails
							status={row.getValue('status')}
						/>
					}
				</div>
			),
		},
	];
};

export default OwnersPageColumns;
